import { useEffect, useRef } from 'react'
import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { jobsApi } from '@/services/jobs'
import { invalidateNotificationQueries } from '@/hooks/useNotifications'
import { invalidatePortalMetricQueries } from '@/hooks/usePortalMetrics'
import type {
  AgendaQuery,
  AgendaSchedule,
  ApiResponse,
  CreateJobRequest,
  DeleteJobScope,
  EditJobRequest,
  JobChangesResponse,
  JobDetail,
  JobListQuery,
  JobReportRequest,
  JobStatusChange,
  RescheduleJobRequest,
  TechnicianSuggestionQuery,
} from '@/types'

export function useAgenda(q: AgendaQuery) {
  return useQuery({
    queryKey: ['agenda', 'schedule', q],
    queryFn: () => jobsApi.agenda(q),
  })
}

// Agenda technician lanes page horizontally like the To Plan tray pages jobs:
// initial render fetches the first technician window, then scroll-near-end
// fetches the next window.
export function useAgendaTechnicianPages(q: AgendaQuery) {
  return useInfiniteQuery({
    queryKey: ['agenda', 'technician-pages', q],
    queryFn: ({ pageParam }) =>
      jobsApi.agenda({ ...q, technician_page: pageParam }),
    initialPageParam: q.technician_page ?? 1,
    getNextPageParam: last => {
      const metadata = last.metadata
      if (!metadata) return undefined
      const { current_page, last_page } = metadata
      return current_page < last_page ? current_page + 1 : undefined
    },
  })
}

/**
 * Poll a small completed-job feed while Agenda is visible and patch matching
 * cards in the existing cache. The full Agenda endpoint is not polled.
 */
export function useAgendaJobChanges(enabled: boolean) {
  const queryClient = useQueryClient()
  const cursorRef = useRef<string | null>(null)
  const pendingChangesRef = useRef(new Map<string, JobStatusChange>())
  const detailFetchedChangesRef = useRef(new Set<string>())

  const query = useQuery<ApiResponse<JobChangesResponse>>({
    queryKey: ['jobs', 'status-changes'],
    queryFn: async () => {
      const cursor =
        cursorRef.current ?? new Date(Date.now() - 60_000).toISOString()
      const response = await jobsApi.changes(cursor)
      if (response.data?.next_cursor) {
        cursorRef.current = response.data.next_cursor
      }
      return response
    },
    enabled,
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: 1,
  })

  const latestChanges = query.data?.data?.changes

  useEffect(() => {
    const changes = latestChanges ?? []
    for (const change of changes) {
      pendingChangesRef.current.set(change.job_id, change)
    }

    const pendingChanges = Array.from(pendingChangesRef.current.values())
    if (pendingChanges.length === 0) return

    const changesByJobId = new Map(
      pendingChanges.map(change => [change.job_id, change])
    )
    const matchedJobIds = new Set<string>()

    const patchSchedule = (
      schedule: AgendaSchedule,
      agendaQuery: AgendaQuery | undefined
    ): AgendaSchedule => {
      let changed = false
      const technicians = schedule.technicians.map(row => {
        const jobs = row.jobs
          .map(job => {
            const change = changesByJobId.get(job.id)
            if (!change) return job

            matchedJobIds.add(change.job_id)
            if (
              job.status === change.status &&
              job.closed_at === change.closed_at &&
              job.updated_at === change.updated_at
            ) {
              return job
            }

            changed = true
            return {
              ...job,
              status: change.status,
              closed_at: change.closed_at,
              updated_at: change.updated_at,
            }
          })
          .filter(
            job => !agendaQuery?.status || job.status === agendaQuery.status
          )

        const jobsChanged =
          jobs.length !== row.jobs.length ||
          jobs.some((job, index) => job !== row.jobs[index])

        return jobsChanged ? { ...row, jobs } : row
      })

      return changed ? { ...schedule, technicians } : schedule
    }

    type AgendaScheduleCache =
      | ApiResponse<AgendaSchedule>
      | InfiniteData<ApiResponse<AgendaSchedule>>

    const isAgendaScheduleQuery = (queryKey: QueryKey) =>
      queryKey[0] === 'agenda' &&
      (queryKey[1] === 'schedule' || queryKey[1] === 'technician-pages')

    const isInfiniteAgendaScheduleCache = (
      cache: AgendaScheduleCache | undefined
    ): cache is InfiniteData<ApiResponse<AgendaSchedule>> =>
      Boolean(cache && 'pages' in cache && Array.isArray(cache.pages))

    const agendaQueries = queryClient.getQueriesData<AgendaScheduleCache>({
      predicate: query => isAgendaScheduleQuery(query.queryKey),
    })

    for (const [queryKey, cachedData] of agendaQueries) {
      if (!cachedData) continue
      const agendaQuery = queryKey[2] as AgendaQuery | undefined

      if (isInfiniteAgendaScheduleCache(cachedData)) {
        let changed = false
        const pages = cachedData.pages.map(page => {
          if (!page.data) return page
          const schedule = patchSchedule(page.data, agendaQuery)
          if (schedule === page.data) return page
          changed = true
          return { ...page, data: schedule }
        })

        if (changed) {
          queryClient.setQueryData(queryKey, { ...cachedData, pages })
        }
        continue
      }

      if (!cachedData.data) continue
      const schedule = patchSchedule(cachedData.data, agendaQuery)
      if (schedule !== cachedData.data) {
        queryClient.setQueryData(queryKey, { ...cachedData, data: schedule })
      }
    }

    for (const change of pendingChanges) {
      queryClient.setQueryData<ApiResponse<JobDetail>>(
        ['jobs', 'detail', change.job_id],
        previous =>
          previous?.data
            ? {
                ...previous,
                data: {
                  ...previous.data,
                  status: change.status,
                  closed_at: change.closed_at,
                  updated_at: change.updated_at,
                },
              }
            : previous
      )
    }

    for (const jobId of matchedJobIds) {
      pendingChangesRef.current.delete(jobId)
    }

    const changesMissingFromAgenda = pendingChanges.filter(
      change =>
        !matchedJobIds.has(change.job_id) &&
        !detailFetchedChangesRef.current.has(change.job_id)
    )

    for (const change of changesMissingFromAgenda) {
      detailFetchedChangesRef.current.add(change.job_id)
    }

    if (changesMissingFromAgenda.length > 0) {
      void Promise.all(
        changesMissingFromAgenda.map(async change => {
          try {
            const detail = await jobsApi.get(change.job_id)
            queryClient.setQueryData<ApiResponse<JobDetail>>(
              ['jobs', 'detail', change.job_id],
              detail
            )
          } catch {
            detailFetchedChangesRef.current.delete(change.job_id)
          }
        })
      )
    }
  }, [latestChanges, query.dataUpdatedAt, queryClient])

  return query
}

// 2.3.2 — the unassigned tray pages through the full set (count can be in the
// hundreds), so it fetches lazily page-by-page. `pageParam` drives the request
// page; `getNextPageParam` stops once the last page has been loaded.
export function useUnassignedJobs(q: JobListQuery) {
  return useInfiniteQuery({
    queryKey: ['agenda', 'unassigned', q],
    queryFn: ({ pageParam }) => jobsApi.unassigned({ ...q, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: last => {
      const { current_page, last_page } = last.metadata
      return current_page < last_page ? current_page + 1 : undefined
    },
  })
}

// Feedback #14 - ranked technician suggestions. Enabled only once there is
// enough context (a scheduled date) to make the ranking meaningful.
export function useTechnicianSuggestions(
  q: TechnicianSuggestionQuery,
  enabled: boolean
) {
  return useQuery({
    queryKey: ['jobs', 'technician-suggestions', q],
    queryFn: () => jobsApi.technicianSuggestions(q),
    enabled,
  })
}

// Shared with the agenda preload queue, which fills this cache entry ahead of
// the click so the detail drawer opens without waiting.
export const jobDetailQueryKey = (id: string | undefined) =>
  ['jobs', 'detail', id] as const

export function useJob(id: string | undefined) {
  return useQuery({
    queryKey: jobDetailQueryKey(id),
    queryFn: () => jobsApi.get(id as string),
    enabled: !!id,
  })
}

export function useCreateJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateJobRequest) => jobsApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agenda'] })
      qc.invalidateQueries({ queryKey: ['jobs'] })
      invalidatePortalMetricQueries(qc)
      invalidateNotificationQueries(qc)
    },
  })
}

export function useUpdateJob(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: EditJobRequest) => jobsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agenda'] })
      qc.invalidateQueries({ queryKey: ['jobs', 'detail', id] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

function invalidateJob(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: ['jobs', 'detail', id] })
  qc.invalidateQueries({ queryKey: ['agenda'] })
  invalidatePortalMetricQueries(qc)
  invalidateNotificationQueries(qc)
}

export function useDeleteJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, scope }: { id: string; scope?: DeleteJobScope }) =>
      jobsApi.remove(id, scope ? { scope } : {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agenda'] })
      invalidatePortalMetricQueries(qc)
    },
  })
}

export function useReassignJob(id: string, clientId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (technician_ids: string[]) =>
      jobsApi.reassign(id, { technician_ids }),
    onSuccess: () => {
      invalidateJob(qc, id)
      if (clientId) {
        qc.invalidateQueries({ queryKey: ['clients', clientId, 'jobs'] })
      }
    },
  })
}

export function useStartJob(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => jobsApi.start(id),
    onSuccess: () => invalidateJob(qc, id),
  })
}

export function useCancelJobStartup(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => jobsApi.cancelStartup(id),
    onSuccess: () => invalidateJob(qc, id),
  })
}

export function useCloseJob(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => jobsApi.close(id),
    onSuccess: () => invalidateJob(qc, id),
  })
}

export function useCancelJob(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cancel_reason: string) =>
      jobsApi.cancel(id, { cancel_reason }),
    onSuccess: () => invalidateJob(qc, id),
  })
}

export function useRescheduleJob(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RescheduleJobRequest) => jobsApi.reschedule(id, body),
    onSuccess: () => invalidateJob(qc, id),
  })
}

export function useJobReport(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['jobs', id, 'report'],
    queryFn: () => jobsApi.getReport(id as string),
    enabled: !!id && enabled,
  })
}

// 2.3.7 / 2.3.9 — per-service-category report option lists. Keyed by the job's
// service_type; disabled until it is known. Labels arrive localized to the
// active UI language (Accept-Language), so they are cached per language via the
// axios instance rather than a query-key locale segment.
export function useReportCatalog(serviceCategory: string | undefined | null) {
  return useQuery({
    queryKey: ['report-catalog', serviceCategory],
    queryFn: () => jobsApi.reportCatalog(serviceCategory ?? undefined),
    enabled: !!serviceCategory,
    staleTime: 5 * 60 * 1000,
  })
}

export function useSaveJobReport(id: string, mode: 'create' | 'edit') {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: JobReportRequest) =>
      mode === 'create'
        ? jobsApi.createReport(id, body)
        : jobsApi.updateReport(id, body),
    onSuccess: (_data, body) => {
      if (body.status === 'completed') {
        qc.setQueryData<ApiResponse<JobDetail>>(['jobs', 'detail', id], prev =>
          prev
            ? ({
                ...prev,
                data: { ...prev.data, status: 'completed' },
              } as ApiResponse<JobDetail>)
            : prev
        )
      }
      qc.invalidateQueries({ queryKey: ['jobs', id, 'report'] })
      qc.invalidateQueries({ queryKey: ['jobs', 'detail', id] })
      qc.invalidateQueries({ queryKey: ['jobs'] })
      qc.invalidateQueries({ queryKey: ['agenda'] })
      invalidatePortalMetricQueries(qc)
      invalidateNotificationQueries(qc)
    },
  })
}
